import type {
  StaffImportIssue,
  StaffImportProcessingState,
  StaffImportReviewRow,
} from "@lafayette/schemas";

import { StatusBadge, type StatusTone } from "../../../ui/status-badge";
import { emptyValue } from "../../components/styles";
import {
  issueMessages,
  type OutcomeView,
  outcomeView,
  processingViews,
  type RowOutcome,
} from "../labels";

// Small parts of the review screens. A state is always a glyph and a word: never colour alone.

const tones: Readonly<Record<OutcomeView["tone"], StatusTone>> = {
  ok: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
  neutral: "neutral",
};

export function ToneBadge({ view }: Readonly<{ view: OutcomeView }>) {
  return (
    <span data-outcome-tone={view.tone} className="inline-flex max-w-full">
      <StatusBadge
        tone={tones[view.tone]}
        glyph={view.glyph}
        className="max-w-full whitespace-normal"
      >
        {view.label}
      </StatusBadge>
    </span>
  );
}

export function OutcomeBadge({ outcome }: Readonly<{ outcome: RowOutcome | null }>) {
  return <ToneBadge view={outcomeView(outcome)} />;
}

export function ProcessingBadge({ state }: Readonly<{ state: StaffImportProcessingState }>) {
  return <ToneBadge view={processingViews[state]} />;
}

const SHOWN_ISSUES = 3;

/** The issues of a row as sentences, each with its severity as a glyph and a word. */
export function IssueList({
  issues,
  limit = SHOWN_ISSUES,
}: Readonly<{
  issues: StaffImportReviewRow["issues"] | readonly StaffImportIssue[];
  limit?: number;
}>) {
  if (issues.length === 0) return <span className={emptyValue}>None</span>;
  const shown = issues.slice(0, limit);
  const rest = issues.length - shown.length;
  return (
    <ul className="m-0 grid list-none gap-1 p-0">
      {shown.map((issue, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a row may have the same code for two fields.
        <li key={index}>
          <span aria-hidden="true">{issue.severity === "ERROR" ? "✕" : "⚠"}</span>{" "}
          <span className="sr-only">{issue.severity === "ERROR" ? "Error: " : "Warning: "}</span>
          {issueMessages[issue.code]}
        </li>
      ))}
      {rest > 0 ? <li className={emptyValue}>and {rest} more</li> : null}
    </ul>
  );
}
